import { useCallback } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import dagre from 'dagre'
import MemoryNode from './MemoryNode'

// Single source of truth for relation type colors — consumed by both
// edge strokes (graph canvas) and RelationTypePill backgrounds.
export const RELATION_COLORS = {
  supersedes:       { stroke: '#d97706', pillBg: 'bg-amber-950/60',   pillText: 'text-amber-400'   }, // amber
  conflicts_with:   { stroke: '#ef4444', pillBg: 'bg-red-950/60',     pillText: 'text-red-400'     }, // red
  synthesized_from: { stroke: '#f97316', pillBg: 'bg-orange-950/60',  pillText: 'text-orange-400'  }, // orange
  related:          { stroke: '#38bdf8', pillBg: 'bg-sky-950/60',     pillText: 'text-sky-400'     }, // sky
  compatible:       { stroke: '#10b981', pillBg: 'bg-emerald-950/60', pillText: 'text-emerald-400' }, // emerald
  scoped:           { stroke: '#14b8a6', pillBg: 'bg-teal-950/60',    pillText: 'text-teal-400'    }, // teal
  evokes:           { stroke: '#7c3aed', pillBg: 'bg-violet-950/60',  pillText: 'text-violet-400'  }, // violet
  not_conflict:     { stroke: '#52525b', pillBg: 'bg-zinc-800',       pillText: 'text-zinc-400'    }, // zinc
}

const NODE_TYPES = { memoryNode: MemoryNode }

const NODE_WIDTH  = 200
const NODE_HEIGHT = 70

function applyDagreLayout(nodes, edges) {
  const g = new dagre.graphlib.Graph()
  g.setDefaultEdgeLabel(() => ({}))
  g.setGraph({ rankdir: 'LR', nodesep: 50, ranksep: 80 })

  nodes.forEach(n => g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT }))
  edges.forEach(e => g.setEdge(e.source, e.target))

  dagre.layout(g)

  return nodes.map(n => {
    const pos = g.node(n.id)
    return {
      ...n,
      position: {
        x: pos.x - NODE_WIDTH  / 2,
        y: pos.y - NODE_HEIGHT / 2,
      },
    }
  })
}

/**
 * Transform the flat traverse result (nodes[] from Rails) into React Flow
 * nodes + edges with dagre LR layout applied.
 */
function buildGraphElements(rawNodes, seedId, onNodeClick) {
  const rfNodes = rawNodes.map(n => ({
    id:   String(n.memory_id),
    type: 'memoryNode',
    data: {
      title:       n.title,
      memory_type: n.memory_type,
      isRoot:      n.memory_id === seedId,
      onClick:     () => onNodeClick(n.memory_id),
    },
    position: { x: 0, y: 0 }, // dagre will overwrite
  }))

  const rfEdges = rawNodes
    .filter(n => n.edge !== null && n.edge !== undefined)
    .map(n => {
      const e     = n.edge
      const color = RELATION_COLORS[e.relation_type] ?? RELATION_COLORS.related
      return {
        id:     `${e.source_id}-${e.target_id}-${e.relation_type}`,
        source: String(e.source_id),
        target: String(e.target_id),
        label:  e.relation_type,
        style:  { stroke: color.stroke },
        labelStyle: { fill: color.pillText?.replace('text-', '').replace('-400', '') ?? '#9090b8', fontSize: 10 },
      }
    })

  const layoutedNodes = applyDagreLayout(rfNodes, rfEdges)
  return { nodes: layoutedNodes, edges: rfEdges }
}

/**
 * MemoryGraph — wraps React Flow + dagre.
 *
 * Props:
 *   seedId  — number: the root memory ID
 *   nodes   — GraphNode[]: raw traverse response from Rails
 *   onNodeClick — (id: number) => void: called when a non-root node is clicked
 */
export default function MemoryGraph({ seedId, nodes: rawNodes, onNodeClick }) {
  const isEmpty = !rawNodes || rawNodes.length <= 1

  const { nodes: layoutedNodes, edges: layoutedEdges } = isEmpty
    ? { nodes: [], edges: [] }
    : buildGraphElements(rawNodes, seedId, onNodeClick)

  const [nodes, , onNodesChange] = useNodesState(layoutedNodes)
  const [edges, , onEdgesChange] = useEdgesState(layoutedEdges)

  if (isEmpty) {
    return (
      <div
        data-testid="memory-graph-empty"
        className="flex items-center justify-center h-64 border border-zinc-700 rounded-lg bg-zinc-800/30"
      >
        <div className="text-center space-y-2">
          <p className="text-sm font-mono text-zinc-500">No relations found.</p>
          <p className="text-xs font-mono text-zinc-600">This memory has no connections in the graph.</p>
        </div>
      </div>
    )
  }

  return (
    <div
      data-testid="memory-graph"
      data-node-count={nodes.length}
      className="memory-graph-canvas border border-zinc-700 rounded-lg overflow-hidden"
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={NODE_TYPES}
        fitView
        colorMode="dark"
        className="bg-zinc-800"
      >
        <Background color="#2d2d50" gap={24} />
        <Controls />
        <MiniMap
          nodeColor="#2d2d50"
          maskColor="rgba(5,5,10,0.7)"
        />
      </ReactFlow>
    </div>
  )
}
